import { convertAddress } from "../../../helpers/shortAddressConverter";
import { IOfficeTypeInfo } from "@/contracts/IOfficeTypeInfo";

const officeTypes = {
  'Офис страхования': 'офисы продаж',
  'Пункт урегулирования убытков ОСАГО': 'урегулирование убытков',
  'Агентский центр': 'офисы продаж',
  'Региональный центр урегулирования убытков': 'урегулирование убытков',
  'Межрегиональный центр урегулирования убытков': 'урегулирование убытков',
  'Пункт урегулирования убытков': 'урегулирование убытков',
  // eslint-disable-next-line prettier/prettier
  'Франшиза': 'офисы продаж',
  'Совмещенный офис Страхования и Банка': 'офисы продаж',
  'Универсальный офис — СА': 'офисы продаж',
}
const productCompensation = {
  AllProducts: 'Все продукты',
  OnlyOsago: 'Только ОСАГО, по записи',
  OnlyKasko: 'Только КАСКО, по записи',
  OnlyKaskoAndOsago: 'Только КАСКО и ОСАГО, по записи',
  ExceptOsago: 'Все продукты, кроме ОСАГО',
  ExceptKasko: 'Все продукты, кроме КАСКО',
  ExceptKaskoAndOsago: 'Все продукты, кроме КАСКО и ОСАГО',
}

export type Office = {
  CITY: string
  COUNTRY: string
  BRANCH_CODE2: string
  TRANSLITERATED_CITY: string
  TRANSLITERATED_COUNTRY: string
  MODIFICATIONDATE: string
  OFFICEID: string
  timeJson: string
  CREDITCARDSACCEPTED: string
  HASBANKING: string
  howToFind: boolean
  LATITUDE: string
  LONGITUDE: string
  AGENTNAME: string
  ADDRESS: string
  PHONECODE: string
  city: string
  time: string
  PHONENUMBER: string
  productCompensation: keyof typeof productCompensation
  OFFICETYPE: keyof typeof officeTypes
  BEHAVIOURJSON: string
  BEHAVIOURTEXT: string
  NAME?: string
}

export type AdaptedOffice = {
  id: number
  branch_code: string
  name: string
  coords: number[]
  title: string
  address: string
  city: string
  city_translit: string
  updatedAt: string
  country_translit: string
  tel: string
  services: {
    title: string
    tags: ReadonlyArray<{ name: string }>
  }
  time: string
  type: string
  warning: null | { title: string; text: string }
  workSchedule: { title: string; text: string }
  wayToOffice?: { title: string; text: string }
  productCompensation: keyof typeof productCompensation
  officeType: keyof typeof officeTypes
  officeFullUrlPath?: string
}

type Schedule = {
  Day: string
  DayName: string
  ShortDayName: string
  IsWorkingDay: boolean
  StartTime: string
  FinishTime: string
  LunchStartTime: string
  LunchFinishTime: string
  LunchType: string
}

export type ScheduleMicroformat = {
  '@type': string
  dayOfWeek: string
  opens?: string
  closes?: string
}

type TemporarySchedule = {
  Date: string
  IsWorkingDay: boolean
  StartTime: string
  FinishTime: string
  LunchStartTime: string
  LunchFinishTime: string
  LunchType: 'WithLunch' | "WithoutLunch"
}

type TimeJson = {
  IsStandartSchedule: boolean
  Schedule: Schedule[]
  TemporarySchedule: TemporarySchedule[]
}
// Замена да/нет на true/false
const itemAdapter = (item: any) => {
  const helper = {
    ДА: true,
    НЕТ: false,
  }

  Object.entries(item).forEach(([key, value]) => {
    item[key] = helper.hasOwnProperty(value as PropertyKey)
      ? helper[value as keyof typeof helper]
      : value
  })

  return item
}

export const officeListAdapter = (list: Office[]) => list.map(officeAdapter)
export const officeAdapter = (item: Office): AdaptedOffice => {
  const localItem = {...item}
  itemAdapter(item)

  const dateNow = new Date()
  const weekDay = (dateNow.getDay() + 7) % 8
  const dateInString = dateNow.toLocaleDateString()

  const schedule: TimeJson = JSON.parse(item.BEHAVIOURJSON)

  const office: any = {
    services: { title: 'Услуги, которые есть в отделении', tags: [] },
  }

  if (item.CREDITCARDSACCEPTED) {
    office.services.tags.push({ name: 'Возможна оплата картой' })
    office.services.tags.push({
      name: productCompensation.AllProducts,
    })
  }

  if (item.HASBANKING) {
    office.services.tags.push({
      name: 'Офис страхования, предоставляет банковские услуги',
    })
  }

  if (item.howToFind) {
    office.wayToOffice = {
      title: 'Как добраться до офиса',
      text: item.howToFind,
    }
  }

  if (schedule.TemporarySchedule && schedule.TemporarySchedule.length) {
    office.warning = {
      title: 'Временные изменения в графике работы:',
      text: schedule.TemporarySchedule.map(
        (_) =>
          `${_.Date}: ${_.IsWorkingDay
            ? `${_.StartTime}-${_.FinishTime}${_.LunchType === 'WithLunch'
              ? `, перерыв: ${_.LunchStartTime}-${_.LunchFinishTime}`
              : ', без перерыва'}`
            : 'выходной'}`
      ).join('<br>'),
    }
  }

  // const workingTime = schedule.Schedule[weekDay]

  if (schedule.TemporarySchedule && schedule.TemporarySchedule.length) {
    office.time = schedule.TemporarySchedule.reduce((accum: any, it: any) => {
      if (it.Date === dateInString) {
        accum = it.IsWorkingDay
          ? `Работает сегодня ${it.StartTime}-${it.FinishTime}`
          : 'Закрыт сегодня'
      }
      return accum
    }, '')

    if (!office.time) {
      office.time = officeOpenToday(item.BEHAVIOURJSON)
    }
  }

  let tel = ''
  if (item.PHONECODE && item.PHONECODE) {
    tel = `+7 ${item.PHONECODE} ${item.PHONECODE}`
  }

  const scheduleMicroformat: ScheduleMicroformat[] = []

  if (schedule.Schedule) {
    schedule.Schedule.forEach((day) => {
      const {
        Day,
        IsWorkingDay,
        StartTime,
        FinishTime,
        LunchStartTime,
        LunchFinishTime,
        LunchType,
      } = day

      if (!IsWorkingDay) {
        scheduleMicroformat.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `http:\\/\\/schema.org\\/${Day}`,
        })
        return
      }

      if (LunchType === 'WithoutLunch') {
        scheduleMicroformat.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `http:\\/\\/schema.org\\/${Day}`,
          opens: StartTime,
          closes: FinishTime,
        })
      } else {
        scheduleMicroformat.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `http:\\/\\/schema.org\\/${Day}`,
          opens: StartTime,
          closes: LunchStartTime,
        })
        scheduleMicroformat.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `http:\\/\\/schema.org\\/${Day}`,
          opens: LunchFinishTime,
          closes: FinishTime,
        })
      }
    })
  }

  return {
    id: parseInt(item.OFFICEID),
    branch_code: item.BRANCH_CODE2,
    coords: [parseFloat(item.LATITUDE), parseFloat(item.LONGITUDE)],
    title: item.AGENTNAME ?
      item.AGENTNAME.replace(/"(.*)"/, '«$1»') :
      item?.NAME?.replace(/"(.*)"/, '«$1»'),
    address: item.ADDRESS,
    city: item.CITY,
    country: item.COUNTRY,
    city_translit: item.TRANSLITERATED_CITY,
    country_translit: item.TRANSLITERATED_COUNTRY,
    updatedAt: item.MODIFICATIONDATE,
    tel: `+7 ${item.PHONECODE} ${item.PHONENUMBER}`,
    services: { title: 'Услуги, которые есть в отделении', tags: [] },
    time: officeOpenToday(item.BEHAVIOURJSON),
    type: officeTypes[item.OFFICETYPE],
    warning: null,
    workSchedule: {
      title: 'График работы',
      text: '<p>' + item.BEHAVIOURTEXT.replace(/(;\s?)/g, '</p><p>') + '</p>',
    },
    workScheduleMicroformat: scheduleMicroformat,
    ...office,
    originalData: localItem,
  }
}

export const officeOpenToday = (behaviour: string): string => {
  try {
    const timeWork = JSON.parse(behaviour)
    const dayWeek = new Date().getDay() - 1

    const todayWork = timeWork.Schedule[dayWeek]

    if (!todayWork.IsWorkingDay) {
      return 'Закрыт сегодня'
    }

    return (
      'Работает сегодня ' + todayWork.StartTime + '-' + todayWork.FinishTime
    )
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(e)
    }

    return 'Закрыт сегодня'
  }
}
